import { plateFont, numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, IconButton, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlaceIcon from '@mui/icons-material/Place';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';
import { can, isSimplifiedUi } from '../../utils/access';
import { normalizeLanguage } from '../../utils/languages';
import useCalendarState from './hooks/useCalendarState';
import useCalendarColors from './hooks/useCalendarColors';
import applyEventFilters from './hooks/eventFilters';
import { formatDayDate, parseDateKey } from './hooks/dateUtils';
import { loadBand, formatHours, absenceSpanText, employeesById as employeesMap } from './hooks/monthUtils';
import { UNAVAILABILITY_CONFIG } from './calendarConstants';
import { LoadBar } from './MonthDayCell';

const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#9c4221', bg: '#fef3e2' },
  ordered: { color: '#92400e', bg: '#fef3c7' },
  arrived: { color: '#065f46', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

function formatHour(hour) {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function shortDate(key) {
  const date = parseDateKey(key);
  return date ? `${date.getDate()}.${date.getMonth() + 1}.` : '';
}

function eventTimeText(event, dateKey) {
  if (event.local_start_date && event.local_end_date && event.local_start_date !== event.local_end_date) {
    return `${shortDate(event.local_start_date)} ${formatHour(event.local_start_hour)} - ${shortDate(event.local_end_date)} ${formatHour(event.local_end_hour)}`;
  }
  return `${formatHour(event.local_start_hour)} - ${formatHour(event.local_end_hour)}`;
}

function Stat({ label, value, color }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="div" sx={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.disabled' }}>
        {label}
      </Typography>
      <Typography component="div" sx={{ fontSize: '14.5px', fontWeight: 700, ...numFont, color: color || 'text.primary', mt: '1px' }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * The side panel a month cell opens: the day's capacity, its absences and note, and the
 * work orders of the day as cards. The list comes from the same /calendar/ endpoint as the
 * week view, so the panel's filters and colours are exactly the week's.
 */
function MonthDayPeek({ isShortScreen }) {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n?.language);
  const navigate = useNavigate();
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const {
    selectedMonthDay,
    monthData,
    monthVersion,
    filters,
    setSelectedMonthDay,
    setCalendarData,
    switchToDay,
    openScheduleDialogForDay,
    schedulingModeTarget,
    highlightedMaintenanceId,
    highlightedScheduleId,
  } = useCalendarState();
  const { resolveEvent } = useCalendarColors();

  const [loading, setLoading] = React.useState(false);
  const [dayData, setDayData] = React.useState(null);

  const dateKey = selectedMonthDay;
  const day = React.useMemo(() => (monthData?.days || []).find((row) => row.date === dateKey) || null, [monthData, dateKey]);
  const monthEmployees = React.useMemo(() => employeesMap(monthData), [monthData]);

  React.useEffect(() => {
    if (!dateKey) return undefined;
    let cancelled = false;
    // A new day starts empty: the previous day's cards must not sit under the new date
    // while its own fetch is in flight.
    setDayData(null);
    setLoading(true);
    axios
      .get('/api/scheduling/calendar/', { params: { start: dateKey, end: dateKey, view: 'employee' } })
      .then((response) => {
        if (cancelled) return;
        setDayData(response.data);
        // Seed the store with the day: the schedule and absence dialogs opened from this
        // panel run their conflict checks against store.events, which the month grid
        // does not load on its own.
        setCalendarData(response.data);
      })
      .catch((err) => {
        console.error('Error fetching day for month peek:', err);
        if (!cancelled) setDayData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey, monthVersion]);

  if (!dateKey) return null;

  const date = parseDateKey(dateKey);
  const band = day ? loadBand(day) : null;
  const events = dayData ? applyEventFilters(dayData.events || [], filters) : [];
  const hiddenByFilters = dayData ? (dayData.events || []).filter((e) => e.state !== 'cancelled').length - events.length : 0;
  const dayEmployees = {};
  (dayData?.employees || []).forEach((employee) => {
    dayEmployees[employee.id] = employee;
  });
  const bays = {};
  (dayData?.service_bays || []).forEach((bay) => {
    bays[bay.id] = bay;
  });

  const canCreate = can(currentOrganization, 'work_orders.create') && !isSimplifiedUi(currentOrganization);
  const width = isShortScreen ? 320 : 372;

  return (
    <Box
      component="aside"
      data-testid="calendar-month-peek"
      sx={{ width, flexShrink: 0, bgcolor: 'background.paper', borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      {/* header */}
      <Box sx={{ p: isShortScreen ? '10px 14px' : '16px 18px 14px', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="div" data-testid="calendar-month-peek-date" sx={{ fontSize: isShortScreen ? '16px' : '19px', fontWeight: 700, letterSpacing: '-0.3px', fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {formatDayDate(date, language)}
              {day?.is_today && (
                <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, bgcolor: 'secondary.main', color: 'white', px: 0.875, py: '2px', borderRadius: '5px' }}>
                  {t('Today')}
                </Box>
              )}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setSelectedMonthDay(null)} data-testid="calendar-month-peek-close" aria-label={t('Close')}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* capacity */}
      {day && (
        <Box sx={{ p: isShortScreen ? '10px 14px' : '14px 18px', borderBottom: 1, borderColor: 'divider', bgcolor: '#fbfcfd' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.875 }}>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}>
              {t('calendar.month.summary.utilization')}
            </Typography>
            <Typography component="span" sx={{ fontSize: '17px', fontWeight: 700, ...numFont, color: band.text }}>
              {day.percent !== null && day.percent !== undefined ? `${day.percent} %` : '–'}
            </Typography>
          </Box>
          <LoadBar percent={day.percent || 0} color={band.color} height={8} />
          <Box sx={{ display: 'flex', gap: 2, mt: 1.25, flexWrap: 'wrap' }}>
            <Stat label={t('calendar.month.peek.booked')} value={`${formatHours(day.booked_hours, language)} h`} />
            <Stat label={t('calendar.month.peek.free')} value={`${formatHours(day.free_hours, language)} h`} color={!day.is_past && day.free_hours >= 4 ? '#0d9488' : undefined} />
            <Stat label={t('calendar.month.peek.capacity')} value={`${formatHours(day.available_hours, language)} h`} />
            <Stat label={t('Work orders')} value={day.work_orders} />
          </Box>
          {(day.absences || []).length > 0 && (
            <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.625 }}>
              {day.absences.map((absence, index) => {
                const config = UNAVAILABILITY_CONFIG[absence.type] || UNAVAILABILITY_CONFIG.personal;
                const employee = monthEmployees[absence.employee_id] || dayEmployees[absence.employee_id];
                return (
                  <Box key={`${absence.employee_id}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12.5px' }}>
                    <Box component="span" sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: config.bg, color: config.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                      {employee?.initials || '?'}
                    </Box>
                    <Typography component="span" sx={{ fontWeight: 500, fontSize: '12.5px' }}>{employee?.name || ''}</Typography>
                    <Box component="span" sx={{ ml: 'auto', fontSize: '11px', fontWeight: 600, color: config.color, bgcolor: config.bg, px: 0.875, py: '2px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
                      {t(config.labelKey)}
                      {absenceSpanText(absence) ? ` ${absenceSpanText(absence)}` : ''}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {day?.note && (
        <Box sx={{ display: 'flex', gap: 1, px: isShortScreen ? 1.75 : 2.25, py: 1.25, bgcolor: '#e0f2fe', borderBottom: 1, borderColor: 'divider', fontSize: '12.5px', color: '#0c4a6e' }}>
          <StickyNote2OutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Box component="span">
            {day.note}
            {day.notes_count > 1 ? ` (+${day.notes_count - 1})` : ''}
          </Box>
        </Box>
      )}

      {/* work orders */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: isShortScreen ? '10px 12px' : '12px 14px' }}>
        <Typography component="div" sx={{ fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', px: 0.5, pb: 1 }}>
          {t('Work orders')}
          {events.length ? ` · ${events.length}` : ''}
        </Typography>

        {loading && !dayData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center', color: 'text.disabled' }}>
            {hiddenByFilters > 0 ? <VisibilityOffIcon sx={{ fontSize: 30 }} /> : <EventAvailableIcon sx={{ fontSize: 30 }} />}
            <Typography component="div" sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.secondary', mt: 1 }}>
              {hiddenByFilters > 0
                ? t('calendar.month.peek.hiddenByFilters', { count: hiddenByFilters })
                : t('calendar.month.peek.noWork')}
            </Typography>
            {hiddenByFilters === 0 && day && day.available_hours > 0 && (
              <Typography component="div" sx={{ fontSize: '12.5px', mt: 0.5 }}>
                {t('calendar.month.peek.capacityAvailable', { hours: formatHours(day.available_hours, language) })}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
            {events.map((event) => {
              const colors = resolveEvent(event, false);
              const config = colors.stateConfig || {};
              const partsConfig = event.parts_order_status ? PARTS_ORDER_CONFIG[event.parts_order_status] : null;
              const mechanics = (event.employee_ids || []).map((id) => dayEmployees[id]).filter(Boolean);
              const bay = event.service_bay_id ? bays[event.service_bay_id] : null;
              // Same rule and colours as the week cards and the unscheduled sidebar
              const isHighlighted = highlightedScheduleId
                ? highlightedScheduleId === event.id
                : highlightedMaintenanceId === event.maintenance_id;
              return (
                <Box
                  key={event.id}
                  data-testid={`month-peek-event-${event.id}`}
                  data-highlighted={isHighlighted ? 'true' : undefined}
                  onClick={() => navigate(`/maintenances/${event.maintenance_id}`)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderLeft: `3px solid ${config.color || '#a0aec0'}`,
                    borderRadius: '8px',
                    p: '9px 11px',
                    bgcolor: isHighlighted ? 'rgba(237, 137, 54, 0.15)' : 'background.paper',
                    boxShadow: isHighlighted ? '0 0 0 2px #ed8936' : undefined,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isHighlighted ? 'rgba(237, 137, 54, 0.15)' : 'background.default' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mb: '3px', minWidth: 0 }}>
                    <Typography component="span" sx={{ ...plateFont, fontWeight: 700, fontSize: '13px' }}>
                      {event.vehicle_registration || '–'}
                    </Typography>
                    {event.work_number && (
                      <Typography component="span" sx={{ fontSize: '11px', color: 'text.disabled', ...numFont }}>
                        #{event.work_number}
                      </Typography>
                    )}
                    {config.labelKey && (
                      <Box component="span" sx={{ ml: 'auto', fontSize: '10px', fontWeight: 700, px: 0.875, py: '2px', borderRadius: '5px', bgcolor: config.bg, color: config.text, whiteSpace: 'nowrap' }}>
                        {t(config.labelKey)}
                      </Box>
                    )}
                  </Box>
                  {(event.vehicle_manufacturer || event.vehicle_model || event.customer_name) && (
                    <Typography component="div" sx={{ fontSize: '12px', color: 'text.secondary', mb: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[event.vehicle_manufacturer, event.vehicle_model].filter(Boolean).join(' ')}
                      {event.customer_name ? ` · ${event.customer_name}` : ''}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: '11.5px', color: 'text.secondary', flexWrap: 'wrap' }}>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...numFont }}>
                      <ScheduleIcon sx={{ fontSize: 13 }} />
                      {eventTimeText(event, dateKey)}
                    </Box>
                    {mechanics.map((mechanic) => (
                      <Box key={mechanic.id} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="span" sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: mechanic.color, color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 700 }}>
                          {mechanic.initials}
                        </Box>
                        {mechanic.name}
                      </Box>
                    ))}
                  </Box>
                  {(partsConfig || bay || colors.tagName) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                      {partsConfig && (
                        <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 600, px: 0.875, py: '2px', borderRadius: '5px', bgcolor: partsConfig.bg, color: partsConfig.color }}>
                          {t(`parts_order.${event.parts_order_status}_short`)}
                        </Box>
                      )}
                      {colors.tagName && (
                        <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 600, px: 0.875, py: '2px', borderRadius: '5px', border: `1px solid ${colors.tagColor}`, color: 'text.secondary' }}>
                          {colors.tagName}
                        </Box>
                      )}
                      {bay && (
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.375, fontSize: '10.5px', color: 'text.secondary' }}>
                          <PlaceIcon sx={{ fontSize: 12 }} />
                          {bay.name}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* actions */}
      <Box sx={{ p: isShortScreen ? '10px 12px' : '12px 14px', borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.875, bgcolor: '#fbfcfd' }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<OpenInNewIcon />}
          data-testid="calendar-month-peek-open-day"
          onClick={() => switchToDay(date)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.light' } }}
        >
          {t('calendar.month.peek.openDay')}
        </Button>
        {canCreate && (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            data-testid="calendar-month-peek-new-work-order"
            onClick={() => openScheduleDialogForDay(dateKey, schedulingModeTarget || null)}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: '8px', borderColor: 'divider', color: 'text.primary' }}
          >
            {schedulingModeTarget
              ? t('calendar.month.peek.scheduleHere', { workNumber: schedulingModeTarget.work_number })
              : t('calendar.month.peek.newWorkOrder')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default MonthDayPeek;
